import { v4 } from 'uuid';
import { Assert } from '@cotera/utilities';
import {
  datasetRowIdColumn,
  isDuckDbTimestampColumnType,
  normalizeDuckDbTimestamp,
} from '@cotera/sdk/core';
import type { DatasetOperationV20260601 } from '@cotera/api';
import {
  duckDbIdentifier,
  duckDbStringLiteral,
  duckDbValueLiteral,
} from '@cotera/client/app/etc/duckdb';
import type { DatasetOperationColumnType } from '../dataset-columns';

/**
 * Position of a row in the preview.
 *
 * Lives here rather than with the table creation because it is the *replay*
 * that gives it meaning: an inserted row gets a fractional value so it lands
 * between its neighbours, which is why this is the right default order rather
 * than the CSV row number. It exists for manual datasets too, and it stays
 * correct for rows added locally before the next compaction renumbers them.
 */
export const DATASET_PREVIEW_ORDER_COLUMN = '__cotera_preview_order';

/** A column of the materialized preview table. */
export type DatasetPreviewColumn = { name: string; type: string | null };

export const duckDbColumnType = (
  columnType: DatasetOperationColumnType
): string => {
  switch (columnType) {
    case 'string':
      return 'VARCHAR';
    case 'number':
      return 'DOUBLE';
    case 'boolean':
      return 'BOOLEAN';
    case 'date':
      return 'TIMESTAMP';
    default:
      return Assert.unreachable(columnType);
  }
};

/**
 * Where an inserted row lands, as an expression rather than a value.
 *
 * Fractional, so a row inserted after a neighbour sits between it and the next
 * one without renumbering anything. It has to be evaluated *by DuckDB, mid
 * replay* — the neighbour it reads may itself have been inserted by an earlier
 * operation in the same batch — which is why this is SQL and not arithmetic
 * done here.
 */
const previewRowInsertOrderExpression = (
  tableName: string,
  rowIdColumn: string,
  afterRowId: string | null | undefined
): string => {
  const tableSql = duckDbIdentifier(tableName);
  const order = duckDbIdentifier(DATASET_PREVIEW_ORDER_COLUMN);
  if (afterRowId === undefined) {
    return `(SELECT COALESCE(MAX(${order}), 0) + 1 FROM ${tableSql})`;
  }
  if (afterRowId === null) {
    return `(SELECT COALESCE(MIN(${order}), 1) - 0.5 FROM ${tableSql})`;
  }
  return `COALESCE((SELECT ${order} + 0.5 FROM ${tableSql} WHERE CAST(${duckDbIdentifier(
    rowIdColumn
  )} AS VARCHAR) = ${duckDbStringLiteral(
    afterRowId
  )} LIMIT 1), (SELECT COALESCE(MAX(${order}), 0) + 1 FROM ${tableSql}))`;
};

/**
 * An operation dropped because the column it names is not on the artifact — a
 * cell/value write with nowhere to land, or a rename of a column that is gone.
 *
 * Carries the operation type and the column, never the value: this is the
 * writeback of a workflow whose column a `refreshPreviewFromSource` reseed left
 * out of the parquet, and the value could be customer data. The caller turns it
 * into a telemetry event so the skip is observable rather than silent.
 */
export type DatasetPreviewSkippedOperation = {
  readonly operation: 'update_cell' | 'insert_row' | 'rename_column';
  readonly columnName: string;
};

/**
 * A logged value as a literal for a column of `type`.
 *
 * The server's write guard used to admit any timestamp `Date.parse` reads, and
 * V8 reads shapes DuckDB will not cast — Go's `2026-10-20 15:10:10.569 +0000
 * UTC` among them. One of those in the log fails the whole replay with a
 * `Conversion Error`, so it is rewritten to the ISO instant here, the same way
 * the server now normalizes it on the way in.
 */
const columnValueLiteral = (
  value: string | number | boolean | null,
  type: string | null
): string =>
  duckDbValueLiteral(
    typeof value === 'string' &&
      type !== null &&
      isDuckDbTimestampColumnType(type)
      ? normalizeDuckDbTimestamp(value, type)
      : value
  );

/** Everything one operation changes: the rows, and the schema. */
type DatasetOperationEffect = {
  /** Executed in order. Empty when the operation is a no-op for this table. */
  readonly statements: readonly string[];
  /** The schema the table has once they have run. */
  readonly columns: readonly DatasetPreviewColumn[];
  /**
   * Writes dropped because their column is not on the artifact. Absent on all
   * but the missing-column guards in `update_cell` and `insert_row`; the fold
   * normalises it to a concrete list. Orthogonal to the statements/columns
   * exhaustiveness the switch enforces, so it stays optional rather than making
   * every unrelated case restate an empty one.
   */
  readonly skipped?: readonly DatasetPreviewSkippedOperation[];
};

export type DatasetOperationContext = {
  /** Raw (unquoted) name of the table being built. */
  readonly tableName: string;
  /** The schema inherited from the operations replayed so far. */
  readonly columns: readonly DatasetPreviewColumn[];
  /**
   * Column rows are addressed by. Null for legacy artifacts with no durable
   * row id, which makes every row-scoped operation a no-op.
   */
  readonly rowIdColumn: string | null;
  /** Injected so a generated row id is deterministic under test. */
  readonly newId: () => string;
};

/**
 * What one operation does to the preview table and to its schema.
 *
 * The SQL replay and the schema projection used to be two exhaustive switches
 * over this union, in two functions six hundred lines apart, kept in agreement
 * by hand. Returning both from one switch makes that impossible: a new
 * operation variant fails to compile until it has said what it does to the
 * rows *and* what it does to the columns.
 *
 * A `Record<type, handler>` would get exhaustiveness from the mapped type and
 * read better, but dispatching it over a union key hits TypeScript's
 * correlated-union limitation and needs a cast, which this repo forbids. One
 * switch with `Assert.unreachable` buys the same guarantee honestly.
 */
export const datasetOperationEffect = (
  operation: DatasetOperationV20260601,
  { tableName, columns, rowIdColumn, newId }: DatasetOperationContext
): DatasetOperationEffect => {
  const table = duckDbIdentifier(tableName);
  /** Row-scoped operations cannot address anything without a row id. */
  const rowMatchSql =
    rowIdColumn === null
      ? null
      : (rowId: string) =>
          `CAST(${duckDbIdentifier(
            rowIdColumn
          )} AS VARCHAR) = ${duckDbStringLiteral(rowId)}`;

  switch (operation.type) {
    case 'update_cell': {
      if (rowMatchSql === null) {
        return { statements: [], columns };
      }
      // A cell edit against a column this artifact does not have is a no-op,
      // matching the server's warehouse applier. A workflow writeback appends
      // an `update_cell` validated against the dataset's logical schema, but a
      // `refreshPreviewFromSource` reseed rebuilds the parquet from the source
      // query alone — so a written-back column can be absent here even though
      // the operation was valid when appended. Without this guard DuckDB throws
      // `Binder Error: Referenced update column <name> not found in table!`.
      const column = columns.find(
        (candidate) => candidate.name === operation.columnName
      );
      if (column === undefined) {
        return {
          statements: [],
          columns,
          skipped: [
            { operation: 'update_cell', columnName: operation.columnName },
          ],
        };
      }
      return {
        statements: [
          `UPDATE ${table}
           SET ${duckDbIdentifier(operation.columnName)} = ${columnValueLiteral(
            operation.value,
            column.type
          )}
           WHERE ${rowMatchSql(operation.rowId)}`,
        ],
        columns,
      };
    }
    case 'insert_row': {
      if (rowIdColumn === null) {
        return { statements: [], columns };
      }
      const insertColumns = [rowIdColumn];
      const insertValues = [duckDbStringLiteral(operation.rowId ?? newId())];
      const skipped: DatasetPreviewSkippedOperation[] = [];
      for (const [columnName, value] of Object.entries(operation.values)) {
        if (columnName === rowIdColumn) {
          continue;
        }
        // Only columns this artifact actually has, for the same reason as
        // `update_cell` above: a reseed can drop a written-back column while its
        // operation stays in the log. A value with nowhere to land is skipped
        // rather than left to fail the whole INSERT.
        const column = columns.find(
          (candidate) => candidate.name === columnName
        );
        if (column === undefined) {
          skipped.push({ operation: 'insert_row', columnName });
          continue;
        }
        insertColumns.push(columnName);
        insertValues.push(columnValueLiteral(value, column.type));
      }
      insertColumns.push(DATASET_PREVIEW_ORDER_COLUMN);
      insertValues.push(
        previewRowInsertOrderExpression(
          tableName,
          rowIdColumn,
          operation.position?.afterRowId
        )
      );
      return {
        statements: [
          `INSERT INTO ${table} (${insertColumns
            .map(duckDbIdentifier)
            .join(', ')}) VALUES (${insertValues.join(', ')})`,
        ],
        columns,
        skipped,
      };
    }
    case 'delete_row': {
      if (rowMatchSql === null) {
        return { statements: [], columns };
      }
      return {
        statements: [
          `DELETE FROM ${table} WHERE ${rowMatchSql(operation.rowId)}`,
        ],
        columns,
      };
    }
    case 'add_column': {
      const columnType = duckDbColumnType(operation.columnType);
      return {
        statements: [
          // IF NOT EXISTS keeps the replay idempotent: the artifact this
          // replays onto can already carry the column once compaction has
          // folded the operation in, and the operation is still listed until
          // then.
          `ALTER TABLE ${table}
           ADD COLUMN IF NOT EXISTS ${duckDbIdentifier(
             operation.columnName
           )} ${columnType}`,
          ...(operation.defaultValue === undefined
            ? []
            : [
                `UPDATE ${table}
                 SET ${duckDbIdentifier(
                   operation.columnName
                 )} = ${columnValueLiteral(
                  operation.defaultValue,
                  columnType
                )}`,
              ]),
        ],
        columns: [...columns, { name: operation.columnName, type: columnType }],
      };
    }
    case 'rename_column': {
      // The structural counterpart to the `update_cell`/`insert_row` guards
      // above. `ALTER TABLE ... RENAME` has no `IF EXISTS` the way `add_column`
      // and `delete_column` do, so a rename whose source column is missing — or
      // whose target name is already taken — is not a no-op but a hard failure:
      // DuckDB throws `Binder Error: Column "<old>" not found` (or `... already
      // exists`) and takes the *whole* replay down with it, latching the grid on
      // "Could not load parquet preview" with no way back but a reload. The same
      // reseed/compaction gap the value guards exist for reaches renames too — a
      // `refreshPreviewFromSource` reseed rebuilds the parquet from the source
      // query, so a column renamed in the still-uncompacted log can be gone
      // from, or already renamed in, the artifact this replays onto. Skip it and
      // leave the schema as the table actually is.
      const hasOld = columns.some(
        (column) => column.name === operation.oldColumnName
      );
      const hasNew = columns.some(
        (column) => column.name === operation.newColumnName
      );
      if (!hasOld || hasNew) {
        return {
          statements: [],
          columns,
          skipped: [
            {
              operation: 'rename_column',
              columnName: operation.oldColumnName,
            },
          ],
        };
      }
      return {
        statements: [
          `ALTER TABLE ${table}
           RENAME ${duckDbIdentifier(
             operation.oldColumnName
           )} TO ${duckDbIdentifier(operation.newColumnName)}`,
        ],
        columns: columns.map((column) =>
          column.name === operation.oldColumnName
            ? { ...column, name: operation.newColumnName }
            : column
        ),
      };
    }
    case 'delete_column':
      return {
        statements: [
          `ALTER TABLE ${table}
           DROP COLUMN IF EXISTS ${duckDbIdentifier(operation.columnName)}`,
        ],
        columns: columns.filter(
          (column) => column.name !== operation.columnName
        ),
      };
    default:
      return Assert.unreachable(operation);
  }
};

/**
 * Folds a whole operation log into the statements that replay it and the
 * schema it leaves behind.
 *
 * Both the schema and the row-id column thread through, step by step. The
 * schema so that an `add_column` followed by a `rename_column` on the same
 * column resolves the way it would in the table; the row-id column because a
 * `rename_column` or `delete_column` on it changes what *later* operations
 * address rows by. Resolving the id once from the final schema — which is what
 * this used to do — emitted `UPDATE … WHERE "new_name"` for operations that
 * ran while the column was still called something else.
 */
export const datasetOperationsEffect = (
  operations: readonly DatasetOperationV20260601[],
  context: Omit<DatasetOperationContext, 'columns' | 'rowIdColumn'> & {
    /** The schema before any operation has run. */
    columns: readonly DatasetPreviewColumn[];
  }
): DatasetOperationEffect =>
  operations.reduce<DatasetOperationEffect>(
    (accumulated, operation) => {
      const effect = datasetOperationEffect(operation, {
        ...context,
        columns: accumulated.columns,
        rowIdColumn: datasetRowIdColumn(accumulated.columns),
      });
      return {
        statements: [...accumulated.statements, ...effect.statements],
        columns: effect.columns,
        skipped: [...(accumulated.skipped ?? []), ...(effect.skipped ?? [])],
      };
    },
    { statements: [], columns: context.columns, skipped: [] }
  );

/** The default id source. Separate so tests can inject a deterministic one. */
export const datasetOperationNewId = (): string => v4();
